/**
 * นำเข้าตารางเวร (วันหยุดหมุนเวียน) — หัวหน้าวางแผนใน Excel แล้วนำเข้าให้ไว
 *   1. เลือกช่วงวันที่ → 2. วาง (paste) จาก Excel หรือ อัปไฟล์ (.xlsx/.csv)
 *   3. ตรวจก่อนยืนยัน (Preview): จับคู่พนักงาน + แปลรหัส + เตือนรหัสที่อ่านไม่ออก
 *   4. แปลรหัสที่ไม่รู้จัก (ตั้งครั้งเดียว ใช้ซ้ำทุกรอบ) → 5. ยืนยันนำเข้า
 * Round-trip: "ดึงตารางช่วงก่อนหน้า" + "ส่งออกเป็น Excel" เพื่อแก้แล้วนำเข้ากลับ
 *
 * ช่วงวันที่ข้ามเดือนได้ (เช่น รอบจ่าย 26 ส.ค. – 25 ก.ย.) — ตอนนั้นหัวคอลัมน์เป็น
 * "เลขวัน" อย่างเดียวไม่พอ เพราะวันที่ 1 มีได้สองเดือน จึงรองรับหัวคอลัมน์เป็นวันที่
 * เต็ม (YYYY-MM-DD) ด้วย และไฟล์ที่ส่งออกจากระบบจะใส่วันที่เต็มให้อัตโนมัติ
 * ดู docs/SHIFT_ROSTER_BLUEPRINT.md
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { UploadCloud, FileSpreadsheet, Download, CopyPlus, CheckCircle2, AlertTriangle, CalendarDays } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import {
  useRosterImportPreview, useRosterImportCommit, useCodeAlias, useSaveCodeAlias,
  useCopyPrevMonth, useShiftMaster, useOrgDepartments, type RosterImportResult, type RosterImportPayload,
} from '../../hooks';
import { useToast } from '../../components/ui/toast';
import { apiDownload } from '../../lib/api-client';
import {
  DateRangePicker, eachDay, isoOf, countDays, thaiShortDate, type DateRange,
} from '../../components/ui/DateRangePicker';

const OFF_TOKEN = '__OFF__';
const IGNORE_TOKEN = '__IGNORE__';
/** เพดานเดียวกับ MAX_IMPORT_DAYS ฝั่ง worker */
const MAX_IMPORT_DAYS = 92;

/** เดือนถัดไปทั้งเดือน — ค่าเริ่มต้น เพราะงานจัดเวรคือ "วางแผนเดือนหน้า" */
function nextMonthRange(): DateRange {
  const d = new Date();
  return {
    from: isoOf(new Date(d.getFullYear(), d.getMonth() + 1, 1)),
    to: isoOf(new Date(d.getFullYear(), d.getMonth() + 2, 0)),
  };
}

/** "1 ส.ค. 2569 – 31 ส.ค. 2569 (31 วัน)" — โชว์ให้เห็นชัดว่าลงช่วงไหน */
function rangeLabel(r: DateRange): string {
  return `${thaiShortDate(r.from)} – ${thaiShortDate(r.to)}`;
}

/** ดึงเดือนจากชื่อไฟล์ เช่น roster-2026-07.csv → ช่วงทั้งเดือนนั้น (กันตั้งผิดรอบ) */
function rangeFromFileName(name: string): DateRange | null {
  // ไฟล์ที่ระบบส่งออกแบบช่วง: roster-2026-08-26_2026-09-25.csv
  const span = name.match(/(20\d{2}-\d{2}-\d{2})[_](20\d{2}-\d{2}-\d{2})/);
  if (span) return { from: span[1]!, to: span[2]! };
  const m = name.match(/(20\d{2})[-_]?(0[1-9]|1[0-2])(?!\d)/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  return { from: isoOf(new Date(y, mo - 1, 1)), to: isoOf(new Date(y, mo, 0)) };
}

/** parse 1 บรรทัด CSV/TSV → ช่อง (รองรับ tab, และ comma แบบมี quote) */
function splitLine(line: string): string[] {
  if (line.includes('\t')) return line.split('\t');
  const out: string[] = [];
  let cur = '', q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (q) {
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') q = false;
      else cur += c;
    } else if (c === '"') q = true;
    else if (c === ',') { out.push(cur); cur = ''; }
    else cur += c;
  }
  out.push(cur);
  return out;
}

function parseText(text: string): string[][] {
  return text.replace(/\r/g, '').split('\n').filter((l) => l.trim() !== '').map(splitLine);
}

export interface DerivedRows {
  rows: RosterImportPayload['rows'];
  dayCount: number;
  /** หัวคอลัมน์ที่ดูเหมือนวันที่ แต่จับกับช่วงที่เลือกไม่ได้ — บอกผู้ใช้ ไม่ตัดเงียบ */
  unmappedHeaders: string[];
  error?: string;
}

/**
 * matrix (คน×วัน) → payload rows โดยแปลงหัวคอลัมน์เป็น "วันที่จริง" ตามช่วงที่เลือก.
 * หัวคอลัมน์รับได้ 2 แบบ:
 *   - วันที่เต็ม "2026-09-01" (ไฟล์ที่ระบบส่งออกตอนช่วงข้ามเดือน)
 *   - เลขวัน "1".."31" — จับเข้ากับวันในช่วง; ถ้าช่วงยาวจนเลขนั้นซ้ำ (มีวันที่ 1
 *     สองเดือน) ถือว่าบอกไม่ได้ ต้องใช้ไฟล์ที่มีวันที่เต็ม (ไม่เดาให้)
 */
export function deriveRows(matrix: string[][], range: DateRange): DerivedRows {
  const empty = { rows: [], dayCount: 0, unmappedHeaders: [] };
  if (matrix.length < 2) return { ...empty, error: 'ต้องมีอย่างน้อยแถวหัว (วันที่) + 1 แถวข้อมูล' };
  if (!range.from || !range.to) return { ...empty, error: 'ยังไม่ได้เลือกช่วงวันที่' };

  const dates = eachDay(range.from, range.to).map(isoOf);
  const dateSet = new Set(dates);
  const dayIndex = new Map<number, string | 'ambiguous'>();
  for (const d of dates) {
    const day = Number(d.slice(8, 10));
    dayIndex.set(day, dayIndex.has(day) ? 'ambiguous' : d);
  }

  const header = matrix[0]!;
  const dayCols: Array<{ col: number; date: string }> = [];
  const unmappedHeaders: string[] = [];
  let firstDayCol = -1;
  header.forEach((h, col) => {
    const raw = String(h ?? '').trim();
    if (!raw) return;
    let date: string | null = null;
    const isoMatch = raw.match(/(20\d{2}-\d{2}-\d{2})/);
    if (isoMatch) {
      date = dateSet.has(isoMatch[1]!) ? isoMatch[1]! : null;
      if (!date) unmappedHeaders.push(raw);
    } else {
      const m = raw.match(/\d+/);
      const day = m ? Number(m[0]) : NaN;
      if (!Number.isInteger(day) || day < 1 || day > 31) return; // ไม่ใช่คอลัมน์วัน
      const hit = dayIndex.get(day);
      if (!hit || hit === 'ambiguous') unmappedHeaders.push(raw);
      else date = hit;
    }
    if (date) {
      dayCols.push({ col, date });
      if (firstDayCol === -1) firstDayCol = col;
    } else if (firstDayCol === -1 && unmappedHeaders.length > 0) {
      // คอลัมน์วันที่ที่จับไม่ได้ ก็ยังนับเป็นจุดเริ่มของโซนวัน (คอลัมน์ชื่ออยู่ก่อนหน้า)
      firstDayCol = col;
    }
  });
  if (dayCols.length === 0) {
    return {
      ...empty,
      unmappedHeaders,
      error: unmappedHeaders.length
        ? 'วันที่ในไฟล์ไม่ตรงกับช่วงที่เลือก — แก้ช่วงวันที่ให้ตรงกับไฟล์ หรือกด "ส่งออกเป็น Excel" เอาเทมเพลตของช่วงนี้ไปกรอก'
        : 'ไม่พบคอลัมน์วันที่ — แถวแรกต้องเป็นเลขวันที่ (1, 2, 3, …) หรือวันที่เต็ม (2026-09-01)',
    };
  }
  const idCols = header.map((_, col) => col).filter((col) => col < firstDayCol);
  if (idCols.length === 0) return { ...empty, unmappedHeaders, error: 'ไม่พบคอลัมน์ชื่อ/รหัสพนักงาน (ต้องอยู่ก่อนคอลัมน์วันที่)' };

  const rows: RosterImportPayload['rows'] = [];
  for (let r = 1; r < matrix.length; r++) {
    const row = matrix[r]!;
    const key = idCols.map((c) => String(row[c] ?? '').trim()).find((v) => v !== '') ?? '';
    if (!key) continue;
    const cells: Record<string, string> = {};
    for (const { col, date } of dayCols) {
      const v = String(row[col] ?? '').trim();
      if (v !== '') cells[date] = v;
    }
    rows.push({ key, cells });
  }
  return { rows, dayCount: dayCols.length, unmappedHeaders };
}

export function RosterImport() {
  const [range, setRange] = useState<DateRange>(nextMonthRange);
  const [dept, setDept] = useState('');
  const { data: orgDepts } = useOrgDepartments();
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<RosterImportResult | null>(null);
  const [aliasDraft, setAliasDraft] = useState<Record<string, string>>({});

  const toast = useToast();
  const shifts = useShiftMaster();
  const codeAlias = useCodeAlias();
  const previewM = useRosterImportPreview();
  const commitM = useRosterImportCommit();
  const saveAlias = useSaveCodeAlias();
  const copyPrev = useCopyPrevMonth();

  const parsed = useMemo(() => deriveRows(parseText(text), range), [text, range]);

  function payload(): RosterImportPayload { return { from: range.from, to: range.to, rows: parsed.rows }; }

  async function onFile(file: File) {
    setFileName(file.name);
    // เดาช่วงจากชื่อไฟล์ (roster-2026-07 / roster-2026-08-26_2026-09-25) → กันลงผิดรอบ
    const fr = rangeFromFileName(file.name);
    if (fr && (fr.from !== range.from || fr.to !== range.to)) { setRange(fr); setPreview(null); }
    if (/\.(xlsx|xls)$/i.test(file.name)) {
      try {
        const XLSX = await import('xlsx');
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]!]!;
        const aoa = XLSX.utils.sheet_to_json<string[]>(ws, { header: 1, raw: false, defval: '' });
        setText(aoa.map((r) => (r ?? []).join('\t')).join('\n'));
      } catch {
        toast.error('อ่านไฟล์ Excel ไม่สำเร็จ — ลองบันทึกเป็น .csv แล้วอัปใหม่');
      }
    } else {
      setText(await file.text());
    }
  }

  function runPreview() {
    if (parsed.error) return toast.error(parsed.error);
    if (parsed.rows.length === 0) return toast.error('ไม่พบข้อมูลในตาราง');
    previewM.mutate(payload(), {
      onSuccess: (r) => { setPreview(r); setAliasDraft({}); },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'ตรวจไม่สำเร็จ'),
    });
  }

  function saveAliasesAndRepreview() {
    const merged = { ...(codeAlias.data?.aliases ?? {}) };
    let n = 0;
    for (const [raw, val] of Object.entries(aliasDraft)) {
      if (val) { merged[raw] = val; n++; }
    }
    if (n === 0) return toast.error('ยังไม่ได้เลือกการแปลรหัส');
    saveAlias.mutate(merged, {
      onSuccess: () => { toast.success('บันทึกการแปลรหัสแล้ว'); runPreview(); },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'),
    });
  }

  function runCommit() {
    previewM.reset();
    commitM.mutate(payload(), {
      onSuccess: (r) => {
        const rm = r.diagnostics?.roster_mode_set ?? 0;
        toast.success(`นำเข้าตารางเวร ${rangeLabel(range)} แล้ว ${r.written} ช่อง${rm ? ` · ตั้งโหมดตารางเวรให้ ${rm} คน` : ''}`);
        setPreview(null); setText(''); setFileName('');
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'นำเข้าไม่สำเร็จ'),
    });
  }

  const d = preview?.diagnostics;
  const shiftOpts = (shifts.data ?? []).filter((s) => s.active);
  const rangeDays = countDays(range.from, range.to);
  const overLimit = rangeDays > MAX_IMPORT_DAYS;

  return (
    <AdminLayout title="นำเข้าตารางเวร">
      <div className="max-w-3xl mx-auto space-y-4 pb-12">
        <p className="text-sm text-gray-500 px-1">
          วางแผนกะใน Excel ได้เลย แล้วนำเข้าทีเดียว — ตารางแบบ <b>ชื่อ/รหัสพนักงาน × วันที่</b>
          (แถวแรกเป็นเลขวันที่ 1–31 หรือวันที่เต็มแบบ 2026-09-01 ถ้าช่วงคร่อมสองเดือน,
          ช่องเขียน “เช้า/บ่าย/ดึก/หยุด” ตามจริง).
          ระบบจะตรวจและให้ดูก่อนยืนยันเสมอ. พนักงานที่จะใช้ตารางนี้คิดเงิน ต้องตั้งโหมดเป็น “ตารางเวร” ในหน้าพนักงาน.
        </p>

        {/* ช่วงวันที่ + ปุ่ม round-trip */}
        <div className="card p-4 space-y-3">
          {/* แบนเนอร์ช่วงปลายทาง — เด่นชัดกันลงผิดรอบ */}
          <div className="rounded-xl bg-gradient-to-r from-pink/10 to-peach/20 border border-pink/30 px-4 py-3 flex items-center gap-3">
            <CalendarDays className="w-6 h-6 text-pink shrink-0" />
            <div className="leading-tight">
              <div className="text-xs text-gray-500">กำลังจะลงตารางเวรของช่วง</div>
              <div className="text-lg font-bold text-gray-800">{rangeLabel(range)}</div>
              <div className="text-xs text-gray-500">{rangeDays} วัน</div>
            </div>
          </div>
          {overLimit && (
            <p className="text-xs text-red-600">
              นำเข้าได้ครั้งละไม่เกิน {MAX_IMPORT_DAYS} วัน — แบ่งนำเข้าเป็นช่วงย่อย
            </p>
          )}
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[240px]">
              <DateRangePicker
                label="ช่วงวันที่ที่จะลง"
                value={range}
                onChange={(r) => { setRange(r); setPreview(null); }}
                maxDays={MAX_IMPORT_DAYS}
              />
            </div>
            <div>
              <label className="label">เฉพาะแผนก (ทำเป็นกลุ่ม)</label>
              <select value={dept} onChange={(e) => setDept(e.target.value)} className="input">
                <option value="">ทุกแผนก</option>
                {(orgDepts ?? []).map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </div>
            <button
              onClick={() => apiDownload(
                `/hr/shift/roster/export?from=${range.from}&to=${range.to}${dept ? `&dept=${encodeURIComponent(dept)}` : ''}`,
                `roster-${range.from}_${range.to}${dept ? `-${dept}` : ''}.csv`,
              ).catch(() => toast.error('ดาวน์โหลดไม่สำเร็จ'))}
              disabled={overLimit}
              className="btn-secondary inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <Download className="w-4 h-4" /> ส่งออกเป็น Excel (แก้แล้วนำเข้ากลับ)
            </button>
            <button
              onClick={() => copyPrev.mutate({ from: range.from, to: range.to, overwrite: false }, {
                onSuccess: (r) => toast.success(`ดึงตารางช่วง ${thaiShortDate(r.from)} – ${thaiShortDate(r.to)} มาแล้ว ${r.rows} ช่อง — กดส่งออกเพื่อแก้`),
                onError: (e) => toast.error(e instanceof Error ? e.message : 'ดึงไม่สำเร็จ'),
              })}
              disabled={copyPrev.isPending || overLimit}
              className="btn-secondary inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <CopyPlus className="w-4 h-4" /> {copyPrev.isPending ? 'กำลังดึง…' : 'ดึงตารางช่วงก่อนหน้า'}
            </button>
          </div>

          {/* คำแนะนำ: พิมพ์อะไรในช่องได้บ้าง */}
          <div className="rounded-lg bg-teal-50 border border-teal-100 p-3 text-xs text-gray-700">
            <div className="font-medium text-teal-800 mb-1">พิมพ์ในช่องวันที่ได้แบบนี้:</div>
            <ul className="space-y-0.5">
              <li>• <b>ชื่อกะ</b> = วันนั้นเข้ากะนั้น — กะที่ตั้งไว้ตอนนี้:
                {shiftOpts.length ? ' ' : ' (ยังไม่มีกะ — ไปตั้งที่ “กะการทำงาน” ก่อน)'}
                {shiftOpts.map((s, i) => (
                  <span key={s.id} className="font-mono">{i > 0 ? ' · ' : ''}{s.name}</span>
                ))}
              </li>
              <li>• <b>หยุด</b> (หรือ off / ว / x / -) = วันหยุด</li>
              <li>• <b>ลา / พักร้อน</b> = วันลาที่อนุมัติแล้ว (ระบบเติมมาให้ในไฟล์ — อย่าลงกะทับ ถ้าปล่อยไว้ระบบจะข้ามให้เอง)</li>
              <li>• <b>เว้นว่าง</b> = ไม่กำหนด (ใช้กะประจำของคนนั้น ไม่ถูกแตะ)</li>
            </ul>
            <div className="mt-1.5 text-gray-500">
              อยากให้พิมพ์สั้นๆ (เช้า/บ่าย/เย็น/ดึก)? ตั้งชื่อกะให้สั้นแบบนั้นได้เลย หรือใช้ “ตั้งรหัสย่อ” ผูกคำที่หัวหน้าชอบเขียน → กะจริง
            </div>
          </div>
        </div>

        {/* วาง / อัปไฟล์ */}
        <div className="card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <label className="label mb-0">วางตารางจาก Excel (หรืออัปไฟล์)</label>
            <label className="btn-secondary inline-flex items-center gap-1.5 cursor-pointer">
              <FileSpreadsheet className="w-4 h-4" /> เลือกไฟล์
              <input type="file" accept=".xlsx,.xls,.csv,.tsv,.txt" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
            </label>
          </div>
          {fileName && <p className="text-xs text-gray-500">ไฟล์: {fileName}</p>}
          <textarea
            value={text}
            onChange={(e) => { setText(e.target.value); setPreview(null); }}
            rows={8}
            placeholder={'ชื่อ\t1\t2\t3\t4\t5\t6\t7\nสมชาย\tเช้า\tเช้า\tหยุด\tเช้า\tเช้า\tบ่าย\tหยุด\nสมหญิง\tบ่าย\tหยุด\tบ่าย\tบ่าย\tดึก\tดึก\tหยุด'}
            className="input font-mono text-xs w-full"
          />
          {text && !parsed.error && (
            <>
              <p className="text-xs text-teal-700">อ่านได้ {parsed.rows.length} คน · {parsed.dayCount} วัน (จาก {rangeDays} วันในช่วง)</p>
              {parsed.unmappedHeaders.length > 0 && (
                <p className="text-xs text-amber-700">
                  คอลัมน์ที่จับกับช่วงนี้ไม่ได้ {parsed.unmappedHeaders.length} คอลัมน์ (จะไม่ถูกนำเข้า):{' '}
                  {parsed.unmappedHeaders.slice(0, 8).join(' · ')}{parsed.unmappedHeaders.length > 8 ? ' …' : ''}
                  {' '}— ถ้าไฟล์คร่อมสองเดือน หัวคอลัมน์ต้องเป็นวันที่เต็ม (กด “ส่งออกเป็น Excel” เอาเทมเพลตไปกรอก)
                </p>
              )}
            </>
          )}
          {text && parsed.error && <p className="text-xs text-red-600">{parsed.error}</p>}
          <button onClick={runPreview} disabled={previewM.isPending || !text} className="btn-cta w-full justify-center flex items-center gap-2">
            <UploadCloud className="w-4 h-4" /> {previewM.isPending ? 'กำลังตรวจ…' : 'ตรวจก่อนนำเข้า'}
          </button>
        </div>

        {/* ผลตรวจ */}
        {preview && d && (
          <div className="card p-4 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-sm">
              <Stat label="พนักงานจับคู่ได้" value={d.employees_matched} tone="ok" />
              <Stat label="วันทำงาน" value={d.work_cells} tone="ok" />
              <Stat label="วันหยุด" value={d.off_cells} tone="muted" />
              <Stat label="อ่านรหัสไม่ออก" value={d.unknown_cells} tone={d.unknown_cells ? 'warn' : 'ok'} />
            </div>

            {/* ช่องที่หลุดช่วง — บอกให้รู้ ไม่ตัดเงียบ */}
            {((d.out_of_range_cells ?? 0) > 0 || (d.ambiguous_day_cells ?? 0) > 0) && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 text-sm">
                <p className="font-medium text-amber-800 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4" /> มีช่องที่ไม่ถูกนำเข้า
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  {(d.out_of_range_cells ?? 0) > 0 && <>อยู่นอกช่วงที่เลือก {d.out_of_range_cells} ช่อง · </>}
                  {(d.ambiguous_day_cells ?? 0) > 0 && <>บอกไม่ได้ว่าเป็นเดือนไหน {d.ambiguous_day_cells} ช่อง · </>}
                  แก้ช่วงวันที่ให้ตรงกับไฟล์ หรือใช้ไฟล์ที่หัวคอลัมน์เป็นวันที่เต็ม
                </p>
              </div>
            )}

            {/* รหัสที่อ่านไม่ออก → ให้ผูก */}
            {d.unknown_codes.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-2">
                <p className="text-sm font-medium text-amber-800 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4" /> รหัสที่ระบบยังไม่รู้จัก — ผูกให้ครั้งเดียว ใช้ซ้ำทุกเดือน
                </p>
                {d.unknown_codes.map((u) => (
                  <div key={u.raw} className="flex items-center gap-2 text-sm">
                    <code className="bg-white border rounded px-2 py-1 min-w-[80px]">{u.raw}</code>
                    <span className="text-gray-400 text-xs">×{u.count}</span>
                    <span className="text-gray-400">→</span>
                    <select
                      className="input flex-1 py-1"
                      value={aliasDraft[u.raw] ?? ''}
                      onChange={(e) => setAliasDraft((s) => ({ ...s, [u.raw]: e.target.value }))}
                    >
                      <option value="">— เลือก —</option>
                      <option value={OFF_TOKEN}>วันหยุด</option>
                      <option value={IGNORE_TOKEN}>ข้าม (ไม่นำเข้า)</option>
                      {shiftOpts.map((s) => <option key={s.id} value={s.code}>{s.name} ({s.code})</option>)}
                    </select>
                  </div>
                ))}
                <button onClick={saveAliasesAndRepreview} disabled={saveAlias.isPending} className="btn-secondary text-sm">
                  {saveAlias.isPending ? 'กำลังบันทึก…' : 'บันทึกการแปลรหัส แล้วตรวจใหม่'}
                </button>
              </div>
            )}

            {/* ชื่อที่จับคู่ไม่ได้ */}
            {d.unmatched_keys.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50/50 p-3 text-sm">
                <p className="font-medium text-red-700">จับคู่พนักงานไม่ได้ {d.unmatched_keys.length} รายการ (จะถูกข้าม):</p>
                <p className="text-xs text-red-600 mt-1">{d.unmatched_keys.slice(0, 12).join(' · ')}{d.unmatched_keys.length > 12 ? ' …' : ''}</p>
              </div>
            )}

            {/* เตือนกฎหมาย: สัปดาห์ไม่มีวันหยุด */}
            {d.week_warnings.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 text-sm">
                <p className="font-medium text-amber-800 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4" /> มีพนักงานบางสัปดาห์ไม่มีวันหยุดเลย (กฎหมายกำหนดอย่างน้อย 1 วัน/สัปดาห์)
                </p>
                <p className="text-xs text-amber-700 mt-1">{d.week_warnings.map((w) => w.name || w.emp_code).slice(0, 12).join(' · ')}</p>
              </div>
            )}

            <div className="rounded-lg bg-pink/5 border border-pink/20 px-3 py-2 text-sm text-gray-700 flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-pink shrink-0" />
              ตารางนี้จะถูกบันทึกลงช่วง <b className="text-gray-900">{rangeLabel(range)}</b> — ตรวจให้แน่ใจก่อนกดยืนยัน
            </div>
            <button onClick={runCommit} disabled={commitM.isPending || d.writable_entries === 0} className="btn-cta w-full justify-center flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> {commitM.isPending ? 'กำลังนำเข้า…' : `ยืนยันนำเข้าลงช่วง ${rangeLabel(range)} — ${d.writable_entries} ช่อง`}
            </button>
          </div>
        )}

        <div className="text-center"><Link to="/hr/time#shifts" className="text-sm text-teal hover:underline">ไปหน้ากะการทำงาน →</Link></div>
      </div>
    </AdminLayout>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: 'ok' | 'warn' | 'muted' }) {
  const color = tone === 'warn' ? 'text-amber-700' : tone === 'muted' ? 'text-gray-600' : 'text-teal-700';
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50/50 py-2">
      <div className={`text-lg font-semibold ${color}`}>{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}
